import { withConsoleBase } from '../config';
import { assertCurrentSessionGeneration, notifySessionExpired, sessionGeneration } from '../lib/session-events';

export const usageRanges = ['24h', '7d', '30d', '90d'] as const;
export type UsageRange = typeof usageRanges[number];
export type UsageCoverage = 'complete' | 'partial' | 'unavailable';
export interface UsageMetrics {
  requests: number; reported_requests: number; missing_usage_count: number; failed_requests: number;
  input_tokens: number | null; output_tokens: number | null; total_tokens: number | null;
  cached_input_tokens: number | null; reasoning_output_tokens: number | null;
}
export interface UsageGroup extends UsageMetrics { id: string; label: string }
export interface UsageBucket extends UsageMetrics { start: string; end: string; coverage: UsageCoverage }
export interface UsageReport {
  source: 'application_usage_ledger'; observed_at: string;
  window: { range: UsageRange; start: string; end: string; interval: 'hour' | 'day'; end_exclusive: true };
  filters: { model: string | null; agent: string | null; options: { models: Array<{ id: string; label: string }>; agents: Array<{ id: string; label: string }> } };
  totals: UsageMetrics; series: UsageBucket[]; by_model: UsageGroup[]; by_agent: UsageGroup[];
  coverage: { started_at: string | null; status: UsageCoverage; missing_usage_count: number; historical_backfill: false };
}

export class UsageError extends Error {
  constructor(public status: number) { super('AI usage could not be loaded. Try refreshing.'); this.name = 'UsageError'; }
}
const invalid = (): never => { throw new UsageError(502); };
const obj = (v: unknown): Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : invalid();
const count = (v: unknown): number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 ? v : invalid();
const nullableCount = (v: unknown) => v === null ? null : count(v);
const identifier = (v: unknown): string => typeof v === 'string' && /^[A-Za-z0-9][A-Za-z0-9._:/-]{0,127}$/.test(v) ? v : invalid();
const label = (v: unknown): string => typeof v === 'string' && v.length > 0 && v.length <= 128 && !/[\x00-\x1f\x7f]/.test(v) ? v : invalid();
const utc = (v: unknown): string => typeof v === 'string' && /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d{1,6})?(?:Z|\+00:00)$/.test(v) && Number.isFinite(Date.parse(v)) ? v : invalid();
const choice = <T extends string>(v: unknown, allowed: readonly T[]): T => typeof v === 'string' && allowed.includes(v as T) ? v as T : invalid();
const array = (v: unknown, max: number): unknown[] => Array.isArray(v) && v.length <= max ? v : invalid();
function unique<T extends { id: string }>(values: T[]): T[] { return new Set(values.map(v => v.id)).size === values.length ? values : invalid(); }
function metrics(value: unknown): UsageMetrics {
  const v = obj(value);
  const result: UsageMetrics = {
    requests: count(v.requests), reported_requests: count(v.reported_requests), missing_usage_count: count(v.missing_usage_count), failed_requests: count(v.failed_requests),
    input_tokens: nullableCount(v.input_tokens), output_tokens: nullableCount(v.output_tokens), total_tokens: nullableCount(v.total_tokens),
    cached_input_tokens: nullableCount(v.cached_input_tokens), reasoning_output_tokens: nullableCount(v.reasoning_output_tokens),
  };
  if (result.reported_requests + result.missing_usage_count !== result.requests || result.failed_requests > result.requests) invalid();
  // Partial provider fields are summed independently by the ledger. Do not
  // discard known input/output counts or invent a total for incomplete calls.
  if (result.missing_usage_count === 0 && [result.input_tokens, result.output_tokens, result.total_tokens].every(v => v !== null)
    && result.input_tokens! + result.output_tokens! !== result.total_tokens) invalid();
  if (result.missing_usage_count === 0 && result.cached_input_tokens !== null && result.input_tokens !== null && result.cached_input_tokens > result.input_tokens) invalid();
  if (result.missing_usage_count === 0 && result.reasoning_output_tokens !== null && result.output_tokens !== null && result.reasoning_output_tokens > result.output_tokens) invalid();
  return result;
}
export function normalizeUsage(value: unknown): UsageReport {
  const v = obj(value), window = obj(v.window), filters = obj(v.filters), options = obj(filters.options), coverage = obj(v.coverage);
  if (v.source !== 'application_usage_ledger' || window.end_exclusive !== true || coverage.historical_backfill !== false) invalid();
  const start = utc(window.start), end = utc(window.end);
  if (Date.parse(start) >= Date.parse(end)) invalid();
  const option = (value: unknown) => { const v = obj(value); return { id: identifier(v.id), label: label(v.label) }; };
  const group = (value: unknown): UsageGroup => ({ ...option(value), ...metrics(value) });
  const series = array(v.series, 91).map(value => {
    const item = obj(value), start = utc(item.start), end = utc(item.end);
    if (Date.parse(start) >= Date.parse(end)) invalid();
    return { start, end, coverage: choice(item.coverage, ['complete', 'partial', 'unavailable'] as const), ...metrics(item) };
  });
  if (series.some((v, i) => i > 0 && Date.parse(v.start) < Date.parse(series[i - 1].end))) invalid();
  return {
    source: 'application_usage_ledger', observed_at: utc(v.observed_at),
    window: { range: choice(window.range, usageRanges), start, end, interval: choice(window.interval, ['hour', 'day'] as const), end_exclusive: true },
    filters: { model: filters.model === null ? null : identifier(filters.model), agent: filters.agent === null ? null : identifier(filters.agent), options: {
      models: unique(array(options.models, 128).map(option)), agents: unique(array(options.agents, 64).map(option)),
    } },
    totals: metrics(v.totals), series,
    by_model: unique(array(v.by_model, 128).map(group)), by_agent: unique(array(v.by_agent, 64).map(group)),
    coverage: { started_at: coverage.started_at === null ? null : utc(coverage.started_at), status: choice(coverage.status, ['complete', 'partial', 'unavailable'] as const), missing_usage_count: count(coverage.missing_usage_count), historical_backfill: false },
  };
}

export async function fetchUsage(range: UsageRange, model: string | null = null, agent: string | null = null, signal?: AbortSignal): Promise<UsageReport> {
  if (!usageRanges.includes(range)) throw new UsageError(400);
  const params = new URLSearchParams({ range });
  if (model !== null) params.set('model', identifier(model));
  if (agent !== null) params.set('agent', identifier(agent));
  const generation = sessionGeneration();
  let response: Response;
  try { response = await fetch(withConsoleBase('/console-api/v1/usage?' + params), { method: 'GET', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal }); }
  catch { assertCurrentSessionGeneration(generation); throw new UsageError(0); }
  assertCurrentSessionGeneration(generation);
  if (!response.ok) { if (response.status === 401) notifySessionExpired(generation); throw new UsageError(response.status); }
  let payload: unknown;
  try { payload = await response.json(); } catch { assertCurrentSessionGeneration(generation); throw new UsageError(502); }
  assertCurrentSessionGeneration(generation);
  const result = normalizeUsage(payload);
  if (result.window.range !== range || result.filters.model !== model || result.filters.agent !== agent) invalid();
  return result;
}
